import React, { useEffect, useState } from 'react';
import { PublicHeader } from '../components/layout/PublicHeader';
import { PublicFooter } from '../components/layout/PublicFooter';
import { getTeamMembers, TeamMember } from '../services/api';

export const AboutUs: React.FC = () => {
    const [team, setTeam] = useState<TeamMember[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchTeam = async () => {
            try {
                const data = await getTeamMembers();
                setTeam(data);
            } catch (error) {
                console.error("Failed to fetch team members:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchTeam();
    }, []);

    const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);

    return (
        <div className="bg-background-light dark:bg-background-dark min-h-screen flex flex-col transition-colors duration-200 font-body relative">
            <PublicHeader />

            <div className="flex-1 w-full max-w-[1400px] mx-auto px-4 sm:px-10 pt-28 pb-12">
                <div className="bg-white dark:bg-[#1a2332] rounded-xl p-8 border border-gray-200 dark:border-[#282e39] shadow-sm mb-12">
                    <div className="flex flex-col gap-4 text-center items-center">
                        <div className="flex items-center gap-2 text-primary font-bold text-sm tracking-wider uppercase">
                            <span className="material-symbols-outlined text-lg">verified_user</span>
                            <span>Confianza & Seguridad</span>
                        </div>
                        <h1 className="text-slate-900 dark:text-white text-4xl sm:text-5xl font-bold font-display tracking-tight leading-tight">
                            Sobre Nosotros
                        </h1>
                        <p className="text-slate-600 dark:text-[#9da6b9] text-lg max-w-2xl leading-relaxed">
                            En FortifyTech, nuestra misión es asegurar el futuro digital de las empresas mediante soluciones tecnológicas avanzadas y estrategias personalizadas.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-12">
                    <div className="bg-[#161b22] border border-white/5 p-8 rounded-xl">
                        <h2 className="text-2xl font-bold text-white mb-4">Nuestra Historia</h2>
                        <p className="text-gray-400 leading-relaxed mb-4">
                            Fundada en 2024, FortifyTech nació de la necesidad de cerrar la brecha entre la ciberseguridad compleja y las necesidades reales de las empresas modernas. Comenzamos como un pequeño equipo de consultores y hemos crecido hasta convertirnos en un socio tecnológico integral.
                        </p>
                        <p className="text-gray-400 leading-relaxed">
                            Creemos que la tecnología debe ser un habilitador, no un obstáculo. Por eso, nos dedicamos a desmitificar la seguridad informática y hacerla accesible para todos.
                        </p>
                    </div>
                    <div className="bg-[#161b22] border border-white/5 p-8 rounded-xl">
                        <h2 className="text-2xl font-bold text-white mb-4">Nuestros Valores</h2>
                        <ul className="space-y-4">
                            <li className="flex gap-4">
                                <div className="size-10 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400">
                                    <span className="material-symbols-outlined">shield</span>
                                </div>
                                <div>
                                    <h3 className="font-bold text-white">Integridad</h3>
                                    <p className="text-sm text-gray-400">Actuamos con transparencia y honestidad en cada proyecto.</p>
                                </div>
                            </li>
                            <li className="flex gap-4">
                                <div className="size-10 rounded-lg bg-green-500/10 flex items-center justify-center text-green-400">
                                    <span className="material-symbols-outlined">lightbulb</span>
                                </div>
                                <div>
                                    <h3 className="font-bold text-white">Innovación</h3>
                                    <p className="text-sm text-gray-400">Buscamos constantemente nuevas formas de resolver problemas antiguos.</p>
                                </div>
                            </li>
                            <li className="flex gap-4">
                                <div className="size-10 rounded-lg bg-purple-500/10 flex items-center justify-center text-purple-400">
                                    <span className="material-symbols-outlined">groups</span>
                                </div>
                                <div>
                                    <h3 className="font-bold text-white">Colaboración</h3>
                                    <p className="text-sm text-gray-400">Trabajamos codo a codo con nuestros clientes como un solo equipo.</p>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-12 text-center border border-white/5">
                    <h2 className="text-3xl font-bold text-white mb-6">El Equipo</h2>
                    <p className="text-gray-400 max-w-2xl mx-auto mb-10">
                        Contamos con profesionales certificados en CISM, CISSP y CEH, apasionados por la tecnología y comprometidos con su éxito.
                    </p>

                    {loading ? (
                        <div className="flex justify-center p-8">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
                            {team.map((member) => (
                                <div
                                    key={member.id}
                                    onClick={() => setSelectedMember(member)}
                                    className="p-6 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:border-white/20 transition-all cursor-pointer group hover:-translate-y-1"
                                >
                                    <div className="size-20 rounded-full bg-gray-700 mx-auto mb-4 overflow-hidden relative">
                                        {member.image ? (
                                            <img src={member.image} alt={member.name} className="w-full h-full object-cover" />
                                        ) : (
                                            <span className="material-symbols-outlined text-4xl text-gray-500 w-full h-full flex items-center justify-center bg-gray-600">person</span>
                                        )}
                                    </div>
                                    <h3 className="font-bold text-white mb-1 group-hover:text-blue-400 transition-colors">{member.name}</h3>
                                    <p className="text-xs text-brand uppercase tracking-wider font-bold mb-2">{member.role}</p>
                                    <p className="text-xs text-gray-400 mt-2 line-clamp-2">{member.bio}</p>
                                </div>
                            ))}
                            {team.length === 0 && (
                                <p className="col-span-full text-gray-400 italic">No hay miembros del equipo visibles por el momento.</p>
                            )}
                        </div>
                    )}
                </div>

            </div>

            <PublicFooter />

            {/* Team Member Modal */}
            {selectedMember && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setSelectedMember(null)}>
                    <div className="bg-white dark:bg-[#1a2332] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-fade-in border border-gray-200 dark:border-[#282e39]" onClick={e => e.stopPropagation()}>
                        <div className="relative p-8">
                            <button
                                onClick={() => setSelectedMember(null)}
                                className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                            >
                                <span className="material-symbols-outlined text-gray-500">close</span>
                            </button>

                            <div className="flex flex-col md:flex-row gap-8">
                                <div className="flex-shrink-0 mx-auto md:mx-0">
                                    <div className="size-32 rounded-full bg-gray-700 overflow-hidden border-4 border-white dark:border-[#282e39] shadow-lg">
                                        {selectedMember.image ? (
                                            <img src={selectedMember.image} alt={selectedMember.name} className="w-full h-full object-cover" />
                                        ) : (
                                            <span className="material-symbols-outlined text-6xl text-gray-500 w-full h-full flex items-center justify-center bg-gray-600">person</span>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">{selectedMember.name}</h2>
                                    <p className="text-brand font-bold uppercase tracking-wide text-sm mb-4">{selectedMember.role}</p>

                                    <div className="prose dark:prose-invert prose-sm text-slate-600 dark:text-gray-300">
                                        <div className="whitespace-pre-wrap">{selectedMember.bio}</div>
                                    </div>

                                    {selectedMember.linkedin_url && (
                                        <div className="mt-6 pt-6 border-t border-gray-100 dark:border-white/10">
                                            <a
                                                href={selectedMember.linkedin_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-2 text-[#0077b5] hover:underline font-bold"
                                            >
                                                <span className="material-symbols-outlined text-xl">link</span>
                                                Ver Perfil de LinkedIn
                                            </a>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
